'use client';

import TopBanner from '@/components/global/TopBanner';
import PromoBannerDialog from '@/components/global/PromoBannerDialog';
import { withAffiliateBannersCta } from '@/lib/affiliateBanners';
import { resolveFlashBanners } from '@/lib/flashSale';
import { extractAffCode } from '@/lib/launchEvent';
import { useFlashSale } from '@/components/global/FlashSaleProvider';
import type { IPageSettings } from '@/sanity/globals/PageGlobals';

interface AffiliateBannersProps {
  settings?: IPageSettings;
  /**
   * Page CTA — the partner link carrying the coupon / `?aff=` params. When
   * given, both banners point at it instead of their own stored link.
   */
  ctaUrl?: string;
  /**
   * Opt in to the global Flash Sale campaign: while a sale is live its banner
   * replaces the affiliate one, and outside a sale the standard banner stored
   * on the Flash Sale document is used before the page own settings.
   * Left off for the competition pages, which keep their own banners always.
   */
  enableFlashSale?: boolean;
}

/**
 * Top banner + promo popup for affiliate pages. Resolution order (see
 * lib/flashSale.ts): live flash banner → standard banner on the Flash Sale
 * doc → the page own settings.
 */
/** The `?coupon=` of a checkout URL, the other way partner pages carry their code. */
function extractCouponCode(url: string | undefined | null): string | undefined {
  if (!url) return undefined;
  try {
    return new URL(url, 'https://www.iqcapital.io').searchParams.get('coupon') || undefined;
  } catch {
    return undefined;
  }
}

export default function AffiliateBanners({
  settings,
  ctaUrl,
  enableFlashSale = false,
}: AffiliateBannersProps) {
  const { config: flashSale, campaign } = useFlashSale();

  const resolved = resolveFlashBanners(
    settings,
    enableFlashSale ? flashSale : null,
    enableFlashSale ? campaign : null,
  );

  // The partner code of the page: the `?aff=CODE` of its hero CTA (55 of the
  // 59 affiliate pages), or the `?coupon=CODE` the remaining ones carry. The
  // Black Month popup shows it in place of the countdown, so the visitor sees
  // the code the popup's own button already carries.
  const affiliateCode = extractAffCode(ctaUrl) ?? extractCouponCode(ctaUrl);

  // Keeps the partner's coupon / `?aff=` params on whichever banner is shown,
  // exactly as before — a generic banner link would drop the attribution.
  const { topBanner, promoBanner } = withAffiliateBannersCta(
    { topBanner: resolved.topBanner, promoBanner: resolved.promoBanner },
    ctaUrl,
  );

  if (!topBanner && !promoBanner) return null;

  return (
    <>
      {topBanner && <TopBanner data={topBanner} language={settings?.language} />}
      {promoBanner && (
        <PromoBannerDialog
          data={promoBanner}
          language={settings?.language}
          affiliateCode={affiliateCode}
        />
      )}
    </>
  );
}
